import React, { useState, useEffect } from "react";
import {
  Box, Grid, Card, CardContent, Typography, Chip, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, IconButton, Tooltip,
} from "@mui/material";
import {
  CheckCircle, Error, Warning, HubOutlined, Cable, Speed,
  Refresh, TrendingUp, TrendingDown,
} from "@mui/icons-material";
import { hubApi } from "../services/hubApi";

const KPI_CARD_COLORS = {
  success: { bg: "rgba(37,211,102,0.08)", border: "rgba(37,211,102,0.2)", icon: "#25D366" },
  error: { bg: "rgba(239,68,68,0.08)", border: "rgba(239,68,68,0.2)", icon: "#EF4444" },
  warning: { bg: "rgba(245,158,11,0.08)", border: "rgba(245,158,11,0.2)", icon: "#F59E0B" },
  info: { bg: "rgba(107,72,255,0.08)", border: "rgba(107,72,255,0.2)", icon: "#6B48FF" },
};

function KpiCard({ title, value, subtitle, color = "info", icon: Icon }) {
  const c = KPI_CARD_COLORS[color];
  return (
    <Card sx={{ bgcolor: c.bg, border: `1px solid ${c.border}`, borderRadius: 3 }}>
      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Box>
            <Typography sx={{ fontSize: 12, color: "#9CA3AF", mb: 0.5 }}>{title}</Typography>
            <Typography sx={{ fontSize: 28, fontWeight: 700, color: "#fff", lineHeight: 1 }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography sx={{ fontSize: 11, color: "#6B7280", mt: 0.5 }}>{subtitle}</Typography>
            )}
          </Box>
          {Icon && <Icon sx={{ fontSize: 28, color: c.icon, opacity: 0.7 }} />}
        </Box>
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const resp = await hubApi.getDashboard();
      setData(resp.data);
    } catch (err) {
      console.error("Dashboard error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
    const interval = setInterval(loadDashboard, 30000); // Auto-refresh 30s
    return () => clearInterval(interval);
  }, []);

  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress sx={{ color: "#6B48FF" }} />
      </Box>
    );
  }

  if (!data) return null;

  const { connections, hubs, dispatch, billing, recentAlerts } = data;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#fff" }}>
            Dashboard
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
            Visao geral do OtimizAPP Connect
          </Typography>
        </Box>
        <Tooltip title="Atualizar">
          <IconButton onClick={loadDashboard} sx={{ color: "#6B7280" }}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {/* KPIs */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <KpiCard
            title="Canais Ativos"
            value={connections?.byStatus?.CONNECTED || 0}
            subtitle={`${connections?.total || 0} total`}
            color="success"
            icon={Cable}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiCard
            title="Canais em Erro"
            value={(connections?.byStatus?.ERROR || 0) + (connections?.critical?.length || 0)}
            subtitle={connections?.critical?.length ? `${connections.critical.length} criticos` : "Nenhum"}
            color={connections?.critical?.length ? "error" : "success"}
            icon={Error}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiCard
            title="HUBs Online"
            value={hubs?.online || 0}
            subtitle={`${hubs?.total || 0} total, ${hubs?.degraded || 0} degraded`}
            color={hubs?.offline > 0 ? "warning" : "success"}
            icon={HubOutlined}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiCard
            title="Dispatch"
            value={dispatch?.avgLatencyMs ? `${dispatch.avgLatencyMs}ms` : "—"}
            subtitle={`${dispatch?.totalDispatches || 0} total, ${dispatch?.fallbackCount || 0} fallback`}
            color="info"
            icon={Speed}
          />
        </Grid>
      </Grid>

      {/* Billing Status */}
      {billing && (billing.grace > 0 || billing.suspended > 0 || billing.readonly > 0) && (
        <Card sx={{ bgcolor: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.15)", borderRadius: 3, mb: 3 }}>
          <CardContent sx={{ p: 2 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#F59E0B", mb: 1 }}>
              Atencao — Billing
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              {billing.grace > 0 && <Chip size="small" label={`${billing.grace} em grace`} sx={{ bgcolor: "rgba(245,158,11,0.15)", color: "#F59E0B" }} />}
              {billing.suspended > 0 && <Chip size="small" label={`${billing.suspended} suspensos`} sx={{ bgcolor: "rgba(239,68,68,0.15)", color: "#EF4444" }} />}
              {billing.readonly > 0 && <Chip size="small" label={`${billing.readonly} readonly`} sx={{ bgcolor: "rgba(107,72,255,0.15)", color: "#6B48FF" }} />}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Tokens expirando */}
      {connections?.expiringSoon?.length > 0 && (
        <Card sx={{ bgcolor: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.15)", borderRadius: 3, mb: 3 }}>
          <CardContent sx={{ p: 2 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#F59E0B", mb: 1 }}>
              Tokens expirando em 7 dias
            </Typography>
            {connections.expiringSoon.map((c) => (
              <Chip
                key={c.id}
                size="small"
                label={`${c.name} — ${new Date(c.tokenExpiresAt).toLocaleDateString()}`}
                sx={{ mr: 1, mb: 0.5, bgcolor: "rgba(245,158,11,0.1)", color: "#F59E0B" }}
                icon={<Warning sx={{ fontSize: 14, color: "#F59E0B" }} />}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Alertas Recentes */}
      <Card sx={{ bgcolor: "#111827", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 3 }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#fff" }}>
              Alertas Recentes (24h)
            </Typography>
          </Box>
          {recentAlerts?.length === 0 ? (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <CheckCircle sx={{ fontSize: 32, color: "#25D366", mb: 1 }} />
              <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
                Nenhum alerta nas ultimas 24h
              </Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: "#6B7280", fontSize: 11, borderColor: "rgba(255,255,255,0.06)" }}>Nivel</TableCell>
                  <TableCell sx={{ color: "#6B7280", fontSize: 11, borderColor: "rgba(255,255,255,0.06)" }}>Evento</TableCell>
                  <TableCell sx={{ color: "#6B7280", fontSize: 11, borderColor: "rgba(255,255,255,0.06)" }}>Horario</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(recentAlerts || []).slice(0, 10).map((alert, i) => (
                  <TableRow key={i}>
                    <TableCell sx={{ borderColor: "rgba(255,255,255,0.06)" }}>
                      <Chip
                        size="small"
                        label={alert.level}
                        sx={{
                          fontSize: 10, height: 20,
                          bgcolor: alert.level === "critical" ? "rgba(239,68,68,0.15)"
                            : alert.level === "error" ? "rgba(239,68,68,0.1)"
                            : "rgba(245,158,11,0.1)",
                          color: alert.level === "critical" || alert.level === "error" ? "#EF4444" : "#F59E0B",
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: "#D1D5DB", fontSize: 12, borderColor: "rgba(255,255,255,0.06)" }}>
                      {alert.action}
                    </TableCell>
                    <TableCell sx={{ color: "#6B7280", fontSize: 12, borderColor: "rgba(255,255,255,0.06)" }}>
                      {new Date(alert.createdAt).toLocaleTimeString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
